import { openapi } from "../../../../openapi";
import type {
  GetProjectByProjectIdChecklistAndChecklistIdPartsApiResponse,
  GetProjectByProjectIdChecklistAndChecklistIdPartsApiArg,
} from "../../../../openapi";
import { normalizeId } from "@mcapps/shared/sharedUtils";
import { setShowFailedMessageThunk, setShowSuccessMessageThunk } from "../../../global/globalSlice";
import type { AppDispatch } from "../../../store";
import type { IChecklistParts } from "@mctypes/checklist.types";
import { sortArrayByNumber } from "../../utils";
import {
  setChecklistCategoriesInRedux,
  setChecklistItemsInRedux,
  setCurrentChecklistInRedux,
  updateChecklistProgressInRedux,
} from "./checklistSlice";

type ChecklistPartsDraft = GetProjectByProjectIdChecklistAndChecklistIdPartsApiResponse;
type ChecklistPartsArg = GetProjectByProjectIdChecklistAndChecklistIdPartsApiArg;
type ChecklistItemWithProgress = NonNullable<ChecklistPartsDraft["items"]>[number] & {
  progress?: NonNullable<ChecklistPartsDraft["checklist"]>["progress"];
};

const getChecklistPartsArg = (arg: ChecklistPartsArg): ChecklistPartsArg => ({
  projectId: arg.projectId,
  checklistId: arg.checklistId,
});

const patchChecklistParts = (
  dispatch: AppDispatch,
  arg: ChecklistPartsArg,
  recipe: (draft: ChecklistPartsDraft) => void,
) => {
  return dispatch(
    openapi.util.updateQueryData(
      "getProjectByProjectIdChecklistAndChecklistIdParts",
      getChecklistPartsArg(arg),
      recipe,
    ),
  );
};

const setPathValue = (target: Record<string, unknown>, path: string, value: unknown) => {
  const pathParts = path.replace(/^category\./, "").split(".");
  let current = target;

  pathParts.slice(0, -1).forEach((pathPart) => {
    const child = current[pathPart];
    if (child == null || typeof child !== "object") {
      current[pathPart] = {};
    }
    current = current[pathPart] as Record<string, unknown>;
  });

  current[pathParts[pathParts.length - 1]] = value;
};

openapi.enhanceEndpoints({
  endpoints: {
    postProjectByProjectIdChecklistAll: { providesTags: ["Checklist"] },
    getProjectByProjectIdChecklistTemplates: { providesTags: ["Checklist"] },
    getProjectByProjectIdChecklistInstanceAndChecklistId: { providesTags: ["Checklist"] },
    getProjectByProjectIdChecklistTags: { providesTags: ["Checklist"] },
    getProjectByProjectIdChecklistCreatedByUser: { providesTags: ["Checklist"] },
    getChecklistCreatedByUser: { providesTags: ["Checklist"] },
    postProjectByProjectIdChecklistImportExcel: { invalidatesTags: ["Checklist"] },
    postProjectByProjectIdChecklistAdd: {
      invalidatesTags: (_result, error) => error ? [] : ["Checklist"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(setShowFailedMessageThunk("Feil ved oppretting av sjekkliste"));
        }
      },
    },
    getProjectByProjectIdChecklistAndChecklistIdParts: {
      providesTags: ["Checklist"],
    },
    postProjectByProjectIdChecklistUpdateRowPosition: {
      invalidatesTags: ["Checklist"],
    },
    putProjectByProjectIdChecklistAndChecklistId: {
      invalidatesTags: (_result, error) => error ? [] : ["Checklist"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = patchChecklistParts(dispatch, arg, (draft) => {
          if (draft.checklist != null) {
            setPathValue(draft.checklist as Record<string, unknown>, arg.body.attribute, arg.body.newValue);
          }
        });

        try {
          const { data } = await queryFulfilled;
          patchChecklistParts(dispatch, arg, (draft) => {
            draft.checklist = data;
          });
          dispatch(setShowSuccessMessageThunk("Sjekkliste oppdatert"));
        } catch {
          patchResult.undo();
          dispatch(setShowFailedMessageThunk("Feil ved oppdatering av sjekkliste"));
        }
      },
    },
    putProjectByProjectIdChecklistAndChecklistIdUpdateChecklistIds: {
      invalidatesTags: ["Checklist"],
    },
    postProjectByProjectIdChecklistAndChecklistIdCategory: {
      invalidatesTags: ["Checklist"],
    },
    postProjectByProjectIdChecklistAndChecklistIdItem: {
      invalidatesTags: ["Checklist"],
    },
    putProjectByProjectIdChecklistAndChecklistIdCategoryCategoryId: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = patchChecklistParts(dispatch, arg, (draft) => {
          const category = draft.categories.find((item) => item._id === arg.categoryId);
          if (category != null) {
            setPathValue(category as Record<string, unknown>, arg.body.attribute, arg.body.newValue);
          }
        });

        try {
          const { data } = await queryFulfilled;
          patchChecklistParts(dispatch, arg, (draft) => {
            const categoryIndex = draft.categories.findIndex((category) => category._id === data._id);
            if (categoryIndex !== -1) {
              draft.categories[categoryIndex] = data;
            }
          });
          dispatch(setShowSuccessMessageThunk("Sjekkliste oppdatert"));
        } catch {
          patchResult.undo();
          dispatch(setShowFailedMessageThunk("Feil ved oppdatering av sjekklistekategori"));
        }
      },
    },
    deleteProjectByProjectIdChecklistAndChecklistIdCategoryCategoryId: {
      invalidatesTags: ["Checklist"],
    },
    putProjectByProjectIdChecklistAndChecklistIdCategoryCategoryIdInactive: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = patchChecklistParts(dispatch, arg, (draft) => {
          draft.items.forEach((item) => {
            if (normalizeId(item.category) === arg.categoryId) {
              item.inactive = {
                ...item.inactive,
                isChecked: arg.body.inactive,
              };
            }
          });
        });

        try {
          const { data } = await queryFulfilled;
          patchChecklistParts(dispatch, arg, (draft) => {
            Object.assign(draft, data);
          });
          if (data.checklist != null) {
            // The legacy checklist still stores its pre-OpenAPI view types in Redux.
            const legacyParts = data as unknown as IChecklistParts;
            dispatch(updateChecklistProgressInRedux(legacyParts.checklist.progress));
            dispatch(setCurrentChecklistInRedux(legacyParts.checklist));
            dispatch(setChecklistItemsInRedux(sortArrayByNumber(legacyParts.items, "itemId")));
            dispatch(setChecklistCategoriesInRedux(sortArrayByNumber(legacyParts.categories, "categoryId")));
          }
          dispatch(setShowSuccessMessageThunk("Sjekkliste oppdatert"));
        } catch {
          patchResult.undo();
          dispatch(setShowFailedMessageThunk("Feil ved oppdatering av kategori"));
        }
      },
    },
    deleteProjectByProjectIdChecklistAndChecklistIdItemItemId: {
      invalidatesTags: ["Checklist"],
    },
    putProjectByProjectIdChecklistAndChecklistIdItemItemId: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = patchChecklistParts(dispatch, arg, (draft) => {
          const item = draft.items.find((item) => item._id === arg.itemId);
          if (item != null) {
            setPathValue(item as Record<string, unknown>, arg.body.path, arg.body.newValue);
          }
        });

        try {
          const { data } = await queryFulfilled;
          patchChecklistParts(dispatch, arg, (draft) => {
            const { progress, ...updatedItem } = data as ChecklistItemWithProgress;
            const itemIndex = draft.items.findIndex((item) => item._id === data._id);

            if (itemIndex !== -1) {
              draft.items[itemIndex] = updatedItem;
            }

            if (draft.checklist != null && progress != null) {
              draft.checklist.progress = progress;
            }
          });
          dispatch(setShowSuccessMessageThunk("Sjekkliste oppdatert"));
        } catch {
          patchResult.undo();
          dispatch(setShowFailedMessageThunk("Feil ved oppdatering av sjekklisterad"));
        }
      },
    },
    getChecklistsByReviewByReviewId: {
      providesTags: ["Checklist"],
    },
    putChecklistsByChecklistIdDkApprovalToggle: {
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(openapi.util.invalidateTags(["Checklist"]));
        } catch {
          // no-op: leave cache untouched on failed toggle
        }
      },
    },
  },
});
